import { useState } from "react";
import { Lock, CreditCard, ShieldCheck, X, Mail, FileDown, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLocale } from "@/contexts/LocaleContext";
import { toast } from "sonner";
import { generateReceiptPdf } from "@/lib/receiptPdf";

// Format the card number into 4-digit groups while preserving the cursor feel.
const formatCardNumber = (s) =>
  s.replace(/\D/g, "").slice(0, 19).replace(/(.{4})/g, "$1 ").trim();
const formatExp = (s) => {
  const d = s.replace(/\D/g, "").slice(0, 4);
  return d.length <= 2 ? d : `${d.slice(0, 2)}/${d.slice(2)}`;
};

const detectBrand = (digits) => {
  const n = digits.replace(/\D/g, "");
  if (n.startsWith("4")) return "Visa";
  if (/^(5[1-5]|2[2-7])/.test(n)) return "Mastercard";
  if (/^(34|37)/.test(n)) return "Amex";
  if (/^(6011|65)/.test(n)) return "Discover";
  if (/^62/.test(n)) return "UnionPay";
  return null;
};

export function CheckoutDialog({ open, onOpenChange, tour, form, totalPrice, pricePerPerson, tourDays, currency, formatPrice, convertPrice, onSuccess }) {
  const { t } = useLocale();
  const [card, setCard] = useState({ number: "", name: "", exp: "", cvv: "" });
  const [submitting, setSubmitting] = useState(false);

  const brand = detectBrand(card.number);
  const cardValid =
    card.number.replace(/\D/g, "").length >= 13 &&
    card.name.trim().length >= 2 &&
    /^\d{2}\/\d{2}$/.test(card.exp) &&
    card.cvv.replace(/\D/g, "").length >= 3;

  const totalLocal = convertPrice(totalPrice, "USD", currency);

  const submit = async () => {
    if (!cardValid) {
      toast.error(t("checkout.error_invalid_card"));
      return;
    }
    // Payment processing is temporarily disabled.
    // Show a friendly error instead of hitting the backend.
    setSubmitting(true);
    setTimeout(() => {
      toast.error(t("checkout.error_payment_unavailable"));
      setSubmitting(false);
    }, 700);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-none p-0 max-h-[92vh] overflow-y-auto">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-border">
          <DialogTitle className="font-display text-2xl tracking-tighter">
            {t("checkout.title")}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            {t("checkout.subtitle")}
          </DialogDescription>
        </DialogHeader>

        {/* DEMO mode banner — required to clearly mark this is not a real charge */}
        <div className="px-6 py-2.5 bg-amber-500/10 border-b border-amber-500/30 flex items-center gap-2">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
          <span className="font-mono-data text-[10px] uppercase tracking-[0.25em] text-amber-700">
            {t("checkout.unavailable_banner")}
          </span>
        </div>

        <div className="grid md:grid-cols-5 gap-0">
          {/* Order summary */}
          <div className="md:col-span-2 bg-secondary/40 p-6 border-r border-border">
            <div className="font-mono-data text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-4">
              {t("checkout.summary_label")}
            </div>
            <div className="space-y-3 text-sm">
              <Row label={t("checkout.row_tour")} value={tour?.name} multiline />
              <Row label={t("checkout.row_date")} value={form.selected_date} mono />
              <Row label={t("checkout.row_passengers")} value={`${form.passengers} ${t("tours.pax_short")}`} mono />
              <Row label={t("checkout.row_duration")} value={`${tourDays} ${t("tours.days")}`} mono />
              <Row label={t("checkout.row_per_person")} value={formatPrice(convertPrice(pricePerPerson, "USD", currency), currency)} mono />
              <Row label={t("checkout.row_airport")} value={form.origin_airport} mono />
            </div>
            <div className="border-t border-border mt-5 pt-4">
              <div className="flex items-baseline justify-between">
                <span className="font-mono-data text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{t("checkout.total")}</span>
                <span className="font-display text-3xl tracking-tighter font-black text-accent" data-testid="checkout-total">
                  {formatPrice(totalLocal, currency)}
                </span>
              </div>
              {currency !== "USD" && (
                <div className="text-[10px] text-muted-foreground/70 text-right mt-1 font-mono-data">
                  ≈ {formatPrice(totalPrice, "USD")}
                </div>
              )}
            </div>
            <p className="text-[10px] text-muted-foreground/80 mt-5 leading-relaxed flex items-start gap-1.5">
              <ShieldCheck className="h-3 w-3 mt-0.5 shrink-0" />
              {t("checkout.security_note")}
            </p>
          </div>

          {/* Card form */}
          <div className="md:col-span-3 p-6 space-y-4">
            <div className="font-mono-data text-[10px] uppercase tracking-[0.25em] text-accent flex items-center gap-2">
              <CreditCard className="h-3 w-3" /> {t("checkout.card_label")}
            </div>

            <div>
              <Label className="text-[10px] font-mono-data uppercase tracking-wider text-muted-foreground">
                {t("checkout.card_number")}
              </Label>
              <div className="relative mt-1.5">
                <Input
                  data-testid="checkout-card-number"
                  placeholder="4242 4242 4242 4242"
                  value={card.number}
                  onChange={(e) => setCard({ ...card, number: formatCardNumber(e.target.value) })}
                  inputMode="numeric"
                  autoComplete="cc-number"
                  className="rounded-none h-11 font-mono-data pr-16"
                />
                {brand && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">
                    {brand}
                  </span>
                )}
              </div>
            </div>

            <div>
              <Label className="text-[10px] font-mono-data uppercase tracking-wider text-muted-foreground">
                {t("checkout.card_name")}
              </Label>
              <Input
                data-testid="checkout-card-name"
                placeholder={t("checkout.card_name_placeholder")}
                value={card.name}
                onChange={(e) => setCard({ ...card, name: e.target.value.toUpperCase() })}
                autoComplete="cc-name"
                className="rounded-none mt-1.5 h-11"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] font-mono-data uppercase tracking-wider text-muted-foreground">
                  {t("checkout.card_exp")}
                </Label>
                <Input
                  data-testid="checkout-card-exp"
                  placeholder="MM/AA"
                  value={card.exp}
                  onChange={(e) => setCard({ ...card, exp: formatExp(e.target.value) })}
                  inputMode="numeric"
                  autoComplete="cc-exp"
                  className="rounded-none mt-1.5 h-11 font-mono-data"
                />
              </div>
              <div>
                <Label className="text-[10px] font-mono-data uppercase tracking-wider text-muted-foreground">
                  CVV
                </Label>
                <Input
                  data-testid="checkout-card-cvv"
                  placeholder="123"
                  value={card.cvv}
                  onChange={(e) => setCard({ ...card, cvv: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                  inputMode="numeric"
                  autoComplete="cc-csc"
                  type="password"
                  className="rounded-none mt-1.5 h-11 font-mono-data"
                />
              </div>
            </div>

            <Button
              onClick={submit}
              data-testid="checkout-pay-btn"
              disabled={!cardValid || submitting}
              className="w-full h-12 mt-2 rounded-none bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground font-mono-data text-xs uppercase tracking-[0.2em]"
            >
              <Lock className="h-3.5 w-3.5 mr-2" />
              {submitting
                ? t("checkout.processing")
                : `${t("checkout.pay_button")} · ${formatPrice(totalLocal, currency)}`}
            </Button>

            <div className="text-[10px] text-muted-foreground/70 leading-relaxed border-t border-border pt-3 mt-3">
              <strong className="text-foreground">{t("checkout.unavailable_label")}</strong>
              <p className="mt-1">{t("checkout.unavailable_help")}</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Row = ({ label, value, mono, multiline }) => (
  <div className="flex justify-between gap-3">
    <span className="text-xs text-muted-foreground shrink-0">{label}</span>
    <span className={`text-right ${mono ? "font-mono-data text-xs" : "text-sm"} ${multiline ? "break-words" : "break-all"}`}>
      {value || "—"}
    </span>
  </div>
);

// ---------- Receipt overlay (post-payment) ----------
export function ReceiptOverlay({ open, onClose, receipt, tour, formatPrice, locale }) {
  const { t } = useLocale();
  if (!open || !receipt) return null;

  const downloadPdf = async () => {
    try {
      await generateReceiptPdf({ receipt, tour, t, locale });
    } catch (e) {
      console.error("PDF gen failed", e);
      toast.error("Falha ao gerar PDF.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="receipt-overlay"
    >
      <div className="relative w-full max-w-2xl bg-card border border-border my-8">
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 p-2 hover:bg-secondary"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Header with PAID stamp */}
        <div className="px-8 pt-10 pb-6 border-b border-border text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 border border-emerald-500/40 bg-emerald-500/10 text-emerald-600 font-mono-data text-[10px] uppercase tracking-[0.3em] mb-5">
            <ShieldCheck className="h-3 w-3" /> {t("receipt.paid_stamp")}
          </div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tighter font-black leading-[1.05]">
            {t("receipt.title")}
          </h2>
          <div className="font-mono-data text-sm text-muted-foreground mt-3 tracking-wider" data-testid="receipt-order-number">
            {receipt.order_number}
          </div>
        </div>

        <div className="p-8 space-y-6">
          <ReceiptRow label={t("receipt.tour")} value={receipt.tour_name} />
          <ReceiptRow label={t("receipt.date")} value={receipt.selected_date} />
          <ReceiptRow label={t("receipt.passengers")} value={`${receipt.passengers} ${t("tours.pax_short")} · ${receipt.duration_days} ${t("tours.days")}`} />
          <ReceiptRow label={t("receipt.airport")} value={receipt.origin_airport} />
          <ReceiptRow label={t("receipt.card")} value={`${receipt.card_brand} ****${receipt.card_last4}`} />
          <ReceiptRow label={t("receipt.paid_at")} value={new Date(receipt.paid_at).toLocaleString(locale === "pt-BR" ? "pt-BR" : "en-US")} />

          <div className="border-t border-border pt-5 flex items-baseline justify-between">
            <span className="font-mono-data text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
              {t("receipt.total_paid")}
            </span>
            <span className="font-display text-3xl font-black text-accent">
              {formatPrice(receipt.total_price_usd, "USD")}
            </span>
          </div>

          <div className="bg-secondary/40 border border-border px-4 py-3 flex items-start gap-2.5">
            <Mail className="h-3.5 w-3.5 mt-0.5 text-accent shrink-0" />
            <div className="text-xs text-muted-foreground">
              {t("receipt.email_sent").replace("{email}", receipt.user_email)}
            </div>
          </div>

          <p className="text-xs text-muted-foreground">{t("receipt.admin_will_contact")}</p>
        </div>

        <div className="px-8 pb-8 flex flex-wrap gap-3 justify-end">
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="receipt-close"
            className="rounded-none h-10 text-xs uppercase tracking-wider"
          >
            {t("receipt.close")}
          </Button>
          <Button
            onClick={downloadPdf}
            data-testid="receipt-download-pdf"
            className="rounded-none h-10 bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground font-mono-data text-xs uppercase tracking-wider"
          >
            <FileDown className="h-3.5 w-3.5 mr-2" /> {t("receipt.download_pdf")}
          </Button>
        </div>
      </div>
    </div>
  );
}

const ReceiptRow = ({ label, value }) => (
  <div className="flex justify-between gap-3">
    <span className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
    <span className="text-sm text-right">{value || "—"}</span>
  </div>
);
